import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  MenuItem,
  Autocomplete,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { mastersApi } from '../../services/expenseMastersApi';

// Generic list + add/edit dialog for an Expense master.
// Masters are never deleted: rows are activated / deactivated instead.
//
// fields: array, or (editingRow) => array so options can include a record's current
//         (possibly inactive) reference.
//         [{ name, label, type: 'text'|'number'|'select'|'autocomplete'|'switch'|'tags',
//            options: [{ value, label }], required, xs, helperText, disabledOnEdit }]
const MasterManager = ({
  resource,
  title,
  addLabel,
  canEdit,
  columns,
  fields,
  validationSchema,
  emptyValues,
  toForm,
  toPayload,
  extraParams = {},
  toolbar = null,
  searchPlaceholder = 'Search...',
}) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const extraKey = JSON.stringify(extraParams);
  const formFields = typeof fields === 'function' ? fields(editing) : fields;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, showInactive, extraKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await mastersApi.list(resource, {
        ...JSON.parse(extraKey),
        search: debouncedSearch,
        page: page + 1,
        limit: rowsPerPage,
        includeInactive: canEdit && showInactive ? 'true' : undefined,
      });
      setRows(res.data || []);
      setTotal(res.pagination?.totalItems || 0);
    } catch (error) {
      toast.error(`Failed to load ${title.toLowerCase()}: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [resource, title, canEdit, debouncedSearch, page, rowsPerPage, showInactive, extraKey]);

  useEffect(() => { load(); }, [load]);

  const openDialog = (row = null) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const body = toPayload(values);
      const res = editing
        ? await mastersApi.update(resource, editing._id, body)
        : await mastersApi.create(resource, body);
      toast.success(res.message || 'Saved');
      (res.warnings || []).forEach((w) => toast.warn(w));
      closeDialog();
      load();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (row) => {
    const next = !row.active;
    if (!next && !window.confirm(`Deactivate "${row.name || row.code}"? It will no longer be selectable but stays available for history.`)) {
      return;
    }
    try {
      const res = await mastersApi.setActive(resource, row._id, next);
      toast.success(res.message);
      load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const renderField = (field, formik) => {
    const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;
    const error = touched[field.name] && Boolean(errors[field.name]);
    const helperText = (touched[field.name] && errors[field.name]) || field.helperText;
    const disabled = Boolean(editing && field.disabledOnEdit);

    switch (field.type) {
      case 'switch':
        return (
          <FormControlLabel
            control={<Switch checked={Boolean(values[field.name])} onChange={(e) => setFieldValue(field.name, e.target.checked)} />}
            label={field.label}
          />
        );
      case 'select':
        return (
          <TextField
            select fullWidth name={field.name} label={field.label} value={values[field.name] ?? ''}
            onChange={handleChange} onBlur={handleBlur} error={error} helperText={helperText}
            required={field.required} disabled={disabled}
          >
            {!field.required && <MenuItem value=""><em>None</em></MenuItem>}
            {(field.options || []).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        );
      case 'autocomplete':
        return (
          <Autocomplete
            options={field.options || []}
            value={(field.options || []).find((o) => o.value === values[field.name]) || null}
            onChange={(e, opt) => setFieldValue(field.name, opt ? opt.value : '')}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            disabled={disabled}
            renderInput={(params) => (
              <TextField {...params} label={field.label} required={field.required} error={error} helperText={helperText} onBlur={handleBlur} name={field.name} />
            )}
          />
        );
      default:
        return (
          <TextField
            fullWidth name={field.name} label={field.label}
            type={field.type === 'number' ? 'number' : 'text'}
            value={values[field.name] ?? ''} onChange={handleChange} onBlur={handleBlur}
            error={error} helperText={helperText} required={field.required} disabled={disabled}
            inputProps={field.inputProps}
          />
        );
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          size="small" placeholder={searchPlaceholder} value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }}
        />
        {toolbar}
        {canEdit && (
          <FormControlLabel
            control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label="Show inactive"
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()} sx={{ textTransform: 'none' }}>
            {addLabel || `Add ${title}`}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              {columns.map((c) => (
                <TableCell key={c.key} sx={{ color: 'white', fontWeight: 600 }}>{c.label}</TableCell>
              ))}
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
              {canEdit && <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center"><CircularProgress size={28} /></TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} align="center">
                  <Typography variant="body2" color="text.secondary">No records found</Typography>
                </TableCell>
              </TableRow>
            ) : rows.map((row) => (
              <TableRow key={row._id} sx={{ '&:hover': { backgroundColor: 'action.hover' }, opacity: row.active ? 1 : 0.6 }}>
                {columns.map((c) => (
                  <TableCell key={c.key}>{c.render ? c.render(row) : (row[c.key] ?? '')}</TableCell>
                ))}
                <TableCell>
                  <Chip size="small" label={row.active ? 'Active' : 'Inactive'} color={row.active ? 'success' : 'default'} variant="outlined" />
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openDialog(row)}><EditIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title={row.active ? 'Deactivate' : 'Activate'}>
                        <Switch size="small" checked={row.active} onChange={() => handleToggleActive(row)} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">{editing ? `Edit ${title}` : (addLabel || `Add ${title}`)}</Typography>
          <IconButton onClick={closeDialog}><CloseIcon /></IconButton>
        </DialogTitle>
        <Formik
          key={editing ? editing._id : 'new'}
          enableReinitialize
          initialValues={editing ? toForm(editing) : emptyValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  {formFields.map((field) => (
                    <Grid item xs={12} sm={field.xs || 12} key={field.name}>
                      {renderField(field, formik)}
                    </Grid>
                  ))}
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 3 }}>
                <Button onClick={closeDialog} disabled={formik.isSubmitting} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={formik.isSubmitting} sx={{ textTransform: 'none' }}>
                  {formik.isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default MasterManager;
